export interface WorkerPort<TResponse> {
  onmessage: ((event: MessageEvent<TResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: unknown, transfer?: Transferable[]): void;
  terminate(): void;
}

export interface TimerScheduler {
  set(callback: () => void, delayMs: number): unknown;
  clear(handle: unknown): void;
}

export interface BoundedWorkerJob {
  readonly active: boolean;
  cancel(): boolean;
}

const browserTimers: TimerScheduler = {
  set: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clear: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>)
};

export function startBoundedWorkerJob<TRequest, TResponse>(options: {
  createWorker: () => WorkerPort<TResponse>;
  request: TRequest;
  transfer?: Transferable[];
  timeoutMs: number;
  onResult: (response: TResponse) => void;
  onTimeout: () => void;
  onFailure: (error: Error) => void;
  timers?: TimerScheduler;
}): BoundedWorkerJob {
  const timers = options.timers ?? browserTimers;
  let active = true, timer: unknown, worker: WorkerPort<TResponse> | undefined;

  const settle = (callback?: () => void) => {
    if (!active) return false;
    active = false;
    if (timer !== undefined) timers.clear(timer);
    if (worker) {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    }
    callback?.();
    return true;
  };

  try {
    worker = options.createWorker();
    worker.onmessage = (event) => settle(() => options.onResult(event.data));
    worker.onerror = (event) => settle(() => options.onFailure(new Error(event.message || "The isolated worker stopped unexpectedly.")));
    timer = timers.set(() => settle(options.onTimeout), options.timeoutMs);
    worker.postMessage(options.request, options.transfer);
  } catch (error) {
    settle(() => options.onFailure(error instanceof Error ? error : new Error("The isolated worker could not start.")));
  }

  return {
    get active() { return active; },
    cancel: () => settle()
  };
}
